/**
 * Mappers to transform API responses to domain objects
 * This layer handles all the data transformation and date parsing
 */

import type { ReportDetail } from '@/domain/harvestReports';
import { toDate } from '@/utils/date';

import type {
    DailyMeasurement,
    DataQualityInfo,
    DistributionData,
    FeedingInfo,
    HarvestReport,
    QualityData,
    ReportPeriod,
    WeightData,
    WeightHistogramBucket,
} from '../types/domain';

import { mapMaturityCorrection } from './maturityCorrection';

/** The two names the report header shows. Threaded in rather than read off the cage context. */
interface ReportNames {
    locationName?: string;
    cageName?: string;
}

/** The API's timestamp as a Date; one that does not parse stays an invalid Date. */
function apiDate(value: string | Date | undefined): Date {
    return toDate(value) ?? new Date(Number.NaN);
}

/**
 * Maps API response to domain object
 */
export function mapApiResponseToDomain(
    apiResponse: ReportDetail,
    names: ReportNames
): HarvestReport {
    return {
        id: apiResponse.id,
        status: apiResponse.status,
        updatedAt: apiDate(apiResponse.updatedAt),

        // `||` not `??`: both an empty string and `undefined` must fall through to the label.
        location: { name: names.locationName || 'Unknown Location' },
        cage: { name: names.cageName || 'Unknown Cage' },

        reportPeriod: mapReportPeriod(apiResponse.data),
        feedingInfo: mapFeedingInfo(apiResponse),

        weightData: mapWeightData(apiResponse),
        qualityData: mapQualityData(apiResponse.data),

        distributionData: mapDistributionData(apiResponse.data),

        dataQuality: mapDataQuality(apiResponse.data),
    };
}

function mapReportPeriod(data: ReportDetail['data']): ReportPeriod {
    return {
        start: apiDate(data.start),
        stop: apiDate(data.stop),
    };
}

function mapFeedingInfo(apiResponse: ReportDetail): FeedingInfo {
    return {
        lastFeedDay: apiResponse.lastFeedDay,
        slaughterDay: apiResponse.slaughterDay,
    };
}

function mapWeightData(apiResponse: ReportDetail): WeightData {
    const { data } = apiResponse;

    return {
        unitTag: apiResponse.weightUnitTag,
        livingWeight: data.livingWeight,
        starvationWeight: data.starvationWeight,
        roundWeight: data.roundWeight,
        slaughteredWeight: data.slaughteredWeight,

        // Weight losses
        starvationLoss: data.starvationLoss,
        bleedingLoss: data.bleedingLoss,
        guttingWaste: data.guttingWaste,
        guttingWastePct: data.guttingWastePct,

        // Temperature
        temperature: data.temperature,

        // Daily measurements
        dailyMeasurements: mapDailyMeasurements(data.days),
    };
}

function mapDailyMeasurements(days: ReportDetail['data']['days']): DailyMeasurement[] {
    return days.map((day) => ({
        day: day.day,
        count: day.count,
        livingWeight: day.livingWeight,
        livingWeightTrend: day.livingWeightTrend,
        stddev: day.stddev,
    }));
}

function mapQualityData(data: ReportDetail['data']): QualityData | undefined {
    // Only return quality data if all required fields are present
    if (
        data.numMeasQuality !== undefined &&
        data.superiorSharePercentage !== undefined &&
        data.estimateDowngradePercentage !== undefined &&
        data.slaughteredWeightSuperior !== undefined &&
        data.slaughteredWeightDowngrade !== undefined
    ) {
        return {
            numMeasQuality: data.numMeasQuality,
            superiorSharePercentage: data.superiorSharePercentage,
            estimateDowngradePercentage: data.estimateDowngradePercentage,
            slaughteredWeightSuperior: data.slaughteredWeightSuperior,
            slaughteredWeightDowngrade: data.slaughteredWeightDowngrade,
            maturityCorrection: mapMaturityCorrection(
                data.estimateDowngradePercentage,
                data.tmpMaturityDowngradePercentage
            ),
        };
    }

    return undefined;
}

function mapDistributionData(data: ReportDetail['data']): DistributionData {
    return {
        cv: data.cv,
        numMeas: data.numMeas,
        numDaysWithMeas: data.numDaysWithMeas,
        histograms: mapHistograms(data.hists),
    };
}

function mapHistograms(hists: ReportDetail['data']['hists']): WeightHistogramBucket[] {
    return hists.map((hist) => ({
        from: hist.from,
        to: hist.to,
        massPercentage: hist.massPercentage,
        mass: hist.mass,
        countPercentage: hist.countPercentage,
        count: hist.count,
        downgradePct: hist.downgrade_pct,
        notSuperiorSharePct: hist.notSuperiorSharePct,
        superiorSharePct: hist.superiorSharePct,
    }));
}

function mapDataQuality(data: ReportDetail['data']): DataQualityInfo {
    return {
        hasWarning: data.warning || false,
    };
}

/**
 * Type guards to ensure data integrity
 */
export function isValidReportDetail(data: unknown): data is ReportDetail {
    return (
        typeof data === 'object' &&
        data !== null &&
        'id' in data &&
        'data' in data &&
        'status' in data &&
        'updatedAt' in data
    );
}

export function hasQualityData(report: HarvestReport): boolean {
    return report.qualityData !== undefined;
}
